/**
 * "Your tokens" in the token picker (Bez, 2026-10-08, first used by the CADINU Lock token step): the tokens a wallet holds among the
 * active token lists, the token pages list and the tokens the user imported. Pure helpers, no network: the hook in useHeldTokens.ts
 * reads the balances with the app's multicall hooks and hands the rows here.
 */

export type HeldRow<T> = {
  token: T
  /** the balance in whole tokens (display and ordering only; the lock amount is always typed and checked in raw units) */
  amount: number
  /** amount x price in USD, or null when no price is known for the token */
  usd: number | null
}

const symbolOf = (t: unknown) => String((t as { symbol?: string })?.symbol ?? '')

/**
 * Only a balance above zero; holdings with a USD value first (largest value first), then the unpriced ones by amount; ties by symbol.
 * A price of 0 or a non-finite value counts as no price.
 */
export function rankHeld<T>(rows: HeldRow<T>[]): HeldRow<T>[] {
  const valued = (r: HeldRow<T>) => r.usd !== null && Number.isFinite(r.usd) && r.usd > 0
  return rows
    .filter((r) => Number.isFinite(r.amount) && r.amount > 0)
    .map((r) => (valued(r) ? r : { ...r, usd: null }))
    .sort((a, b) => {
      const av = a.usd !== null
      const bv = b.usd !== null
      if (av !== bv) return av ? -1 : 1
      if (av && bv && a.usd !== b.usd) return (b.usd as number) - (a.usd as number)
      if (a.amount !== b.amount) return b.amount - a.amount
      return symbolOf(a.token).localeCompare(symbolOf(b.token))
    })
}

/** A held amount for a 56 px row: 619.8B · 12,345 · 1.203 · 0.003994 */
export function fmtHeldAmount(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0'
  if (n >= 1_000_000) return new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 }).format(n)
  if (n >= 1000) return n.toLocaleString('en-US', { maximumFractionDigits: 0 })
  if (n >= 1) return n.toLocaleString('en-US', { maximumFractionDigits: 4 })
  return Number(n.toPrecision(4)).toString()
}

/** A USD value under a held amount: $1.24M · $12,430 · $1.20 · <$0.01; null when unpriced. */
export function fmtHeldUsd(n: number | null): string | null {
  if (n === null || !Number.isFinite(n) || n <= 0) return null
  if (n < 0.01) return '<$0.01'
  if (n >= 100_000) return `$${new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 2 }).format(n)}`
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: n < 1000 ? 2 : 0, maximumFractionDigits: n < 1000 ? 2 : 0 })}`
}
